/**
 * Every screen this console has, what authority it needs, and whose screen it is.
 *
 * **Why this file exists.** The authority for a screen was stated twice, and the two statements
 * had drifted. `app.tsx` wraps each route in a guard; `landing.tsx` restated the same rules as
 * conditions on its links — among them a ten-term disjunction deciding whether the whole
 * configuration card renders at all. Two consequences, both real:
 *
 * - **Ten of twenty-seven Ops routes had exactly one door**, because a link exists only where
 *   somebody wrote one and `landing.tsx` was the application's only navigation surface. A screen
 *   nobody thought to link was reachable by typing a URL.
 * - **The two statements disagreed.** The Ops card gated on `session.platformStaff` alone while
 *   `RequirePlatformStaff` also requires `role === 'opsUser'`; and every facet condition on that
 *   page was a bare string, so `opsFacets.includes('geograpy')` would have hidden a screen for
 *   ever with nothing failing anywhere.
 *
 * So the table is the source, and three things read it: the sidebar, the landing page, and
 * `screens.drift.test.ts`, which parses `app.tsx`'s own guard wrapping and refuses a row that
 * disagrees with it. That is `shipment.drift.test.ts`'s technique — a list kept in two places is
 * a list that diverges, answered with a parser rather than with a convention.
 *
 * **`facet` is typed `OpsFacetName`, and that is why this file imports contracts.** A misspelled
 * facet becomes a compile error rather than a screen that quietly stops being offered.
 *
 * **Authority and audience are separate fields, deliberately.** Authority is what the guard
 * enforces. Audience is whose screen it is — and the two are not the same: `/orders` and
 * `/statement` sit behind `RequireSession` alone, with no role guard, because the API refuses an
 * Ops user independently. A navigation surface derived from authority alone would therefore
 * offer a Merchant's Orders to every operator, and `landing.tsx`'s own header says why that is
 * worse than offering nothing: "A link that leads to a 403 is worse than no link."
 */
import { OPS_FACETS, type OpsFacetName } from '@tard/contracts';
import type { SessionView } from '../api/client.js';

type LiveSession = Extract<SessionView, { authenticated: true }>;

/**
 * What a route's guard enforces — one member per guard in `auth/`.
 *
 * `accessAuthority` is the one disjunction (`access` **or** platform staff), mirroring
 * `@RequireAccessAuthority()` on the API: platform staff sit above `access` rather than beside
 * it, since they alone grant it (ADR-0067).
 */
export type Authority =
  | { readonly kind: 'session' }
  | { readonly kind: 'staff' }
  | { readonly kind: 'facet'; readonly facet: OpsFacetName }
  | { readonly kind: 'staffAndFacet'; readonly facet: OpsFacetName }
  | { readonly kind: 'accessAuthority' };

/**
 * Whose screen this is. Presentation, never authority — see the header.
 *
 * `'any'` is for a screen both roles legitimately open. Nothing uses it today; it exists so that
 * adding such a screen does not tempt someone to widen `audience` into a second guard.
 */
export type Audience = 'merchant' | 'ops' | 'any';

/**
 * The sidebar's groups, in the order they are shown.
 *
 * Grouped by **responsibility**, which is what `OPS_FACETS`' own ordering is grouped by and for
 * the reason its docblock gives: the grouping is the thing a reader needs. So `money` holds the
 * screens that `hub_finance`, `finance` and `pricing` decide, and `platform` holds `access`,
 * `notifications` and `policy` beside the two staff-only records. `flight` and `platform` both
 * hold staff screens, which is why a group is stated per screen rather than derived from its
 * authority.
 */
export const GROUPS = [
  'mine',
  'flight',
  'money',
  'compliance',
  'merchants',
  'fleet',
  'geography',
  'platform',
] as const;

export type Group = (typeof GROUPS)[number];

export interface Screen {
  readonly path: string;
  /** The label's copy key. Four screens label themselves from `policy.key.*` rather than `ops.*`. */
  readonly labelKey: string;
  /**
   * What the rail calls it, where the card's wording is the wrong part of speech for a nav item.
   *
   * Exactly one screen needs it, and seeing why took a screenshot: `/ops` is labelled
   * `ops.open` — "Open the dispatch board" — which is right on a card's primary button and wrong
   * in a list of nouns, where it was also the longest item and sat directly above "Dispatch
   * console", a different screen. A rail reads as a set of places; a button reads as an act.
   */
  readonly navKey?: string;
  readonly group: Group;
  readonly authority: Authority;
  readonly audience: Audience;
}

/**
 * Every navigable screen, in sidebar order.
 *
 * Each label key is the one `landing.tsx` already used, so the sidebar and the cards cannot read
 * differently about the same screen. Four come from the `policy.key.*` namespace, because the
 * screen is one policy value's own page — the easiest thing to lose in a mechanical port, so
 * they are named here and counted in the test.
 */
export const SCREENS: readonly Screen[] = [
  // What a Merchant signs in for. Guarded by the session alone; the API refuses an Ops user.
  {
    path: '/orders',
    labelKey: 'orders.open',
    // "Open orders" on a card; "Orders" in a list of places. `/ops` had the same defect.
    navKey: 'orders.title',
    group: 'mine',
    authority: { kind: 'session' },
    audience: 'merchant',
  },
  {
    path: '/statement',
    labelKey: 'statement.open',
    navKey: 'statement.title',
    group: 'mine',
    authority: { kind: 'session' },
    audience: 'merchant',
  },

  /*
    An Ops person's own notification settings (P4-3c) — the first `mine` row whose audience is not a
    Merchant, and the group's label already reads for both ("Your account").

    `session` is the authority because there is no Ops-role guard in `auth/` and none is needed: the
    API refuses a Merchant with `@Roles('opsUser')`, exactly as it refuses an Ops user on the two rows
    above. `audience` is what stops the rail offering it to the wrong reader, which is the whole point
    of keeping the two fields apart.
  */
  {
    path: '/me/notifications',
    labelKey: 'me.notifications.title',
    group: 'mine',
    authority: { kind: 'session' },
    audience: 'ops',
  },

  // In flight: the operational boards. All platform staff, all cross-tenant and audited.
  {
    path: '/ops',
    labelKey: 'ops.open',
    navKey: 'ops.title',
    group: 'flight',
    authority: { kind: 'staff' },
    audience: 'ops',
  },
  {
    path: '/ops/dispatch',
    labelKey: 'ops.dispatch.title',
    group: 'flight',
    authority: { kind: 'staff' },
    audience: 'ops',
  },
  {
    path: '/ops/couriers',
    labelKey: 'ops.couriers.title',
    group: 'flight',
    authority: { kind: 'staff' },
    audience: 'ops',
  },
  {
    path: '/ops/sla-breaches',
    labelKey: 'ops.breaches.title',
    group: 'flight',
    authority: { kind: 'staff' },
    audience: 'ops',
  },
  {
    path: '/ops/exceptions',
    labelKey: 'ops.exceptions.title',
    group: 'flight',
    authority: { kind: 'staff' },
    audience: 'ops',
  },

  // Money. `hub_finance` counts cash that has arrived at a Hub; `finance` decides the rules money
  // moves under. Migration 0029's own distinction, and the reason they are separate facets.
  {
    path: '/ops/cash-desk',
    labelKey: 'ops.cashDesk.title',
    group: 'money',
    authority: { kind: 'staffAndFacet', facet: 'hub_finance' },
    audience: 'ops',
  },
  {
    path: '/ops/billing',
    labelKey: 'ops.billing.title',
    group: 'money',
    authority: { kind: 'staffAndFacet', facet: 'finance' },
    audience: 'ops',
  },
  {
    path: '/ops/config/tariffs',
    labelKey: 'ops.config.tariffs.title',
    group: 'money',
    authority: { kind: 'staffAndFacet', facet: 'pricing' },
    audience: 'ops',
  },
  {
    path: '/ops/config/fx-rates',
    labelKey: 'ops.config.fx.title',
    group: 'money',
    authority: { kind: 'facet', facet: 'finance' },
    audience: 'ops',
  },
  {
    path: '/ops/config/vat',
    labelKey: 'ops.config.vat.title',
    group: 'money',
    authority: { kind: 'facet', facet: 'finance' },
    audience: 'ops',
  },
  {
    path: '/ops/config/vat-registration',
    labelKey: 'policy.key.vat_registration',
    group: 'money',
    authority: { kind: 'facet', facet: 'finance' },
    audience: 'ops',
  },
  {
    path: '/ops/config/remittance-rail',
    labelKey: 'policy.key.remittance_rail',
    group: 'money',
    authority: { kind: 'facet', facet: 'finance' },
    audience: 'ops',
  },

  // Compliance (I7). The trail is staff; the three settings answer to the compliance facet alone,
  // because each table is platform-scoped and approaches no tenant boundary (ADR-0071 §4).
  {
    path: '/ops/compliance',
    labelKey: 'ops.compliance.title',
    group: 'compliance',
    authority: { kind: 'staff' },
    audience: 'ops',
  },
  {
    path: '/ops/config/screening-policy',
    labelKey: 'ops.screeningPolicy.title',
    group: 'compliance',
    authority: { kind: 'facet', facet: 'compliance' },
    audience: 'ops',
  },
  {
    path: '/ops/config/cod-custody-licence',
    labelKey: 'policy.key.cod_custody_licence',
    group: 'compliance',
    authority: { kind: 'facet', facet: 'compliance' },
    audience: 'ops',
  },
  {
    path: '/ops/config/screening-validity',
    labelKey: 'policy.key.screening_validity',
    group: 'compliance',
    authority: { kind: 'facet', facet: 'compliance' },
    audience: 'ops',
  },

  {
    path: '/ops/directory',
    labelKey: 'ops.directory.title',
    group: 'merchants',
    authority: { kind: 'facet', facet: 'directory' },
    audience: 'ops',
  },
  {
    path: '/ops/catalogue',
    labelKey: 'ops.catalogue.title',
    group: 'merchants',
    authority: { kind: 'facet', facet: 'catalogue' },
    audience: 'ops',
  },

  {
    path: '/ops/fleet',
    labelKey: 'ops.fleet.title',
    group: 'fleet',
    authority: { kind: 'facet', facet: 'fleet' },
    audience: 'ops',
  },

  {
    path: '/ops/geography',
    labelKey: 'ops.geography.title',
    group: 'geography',
    authority: { kind: 'facet', facet: 'geography' },
    audience: 'ops',
  },

  // The platform's own administration.
  {
    path: '/ops/people',
    labelKey: 'ops.people.title',
    group: 'platform',
    authority: { kind: 'accessAuthority' },
    audience: 'ops',
  },
  {
    path: '/ops/notifications',
    labelKey: 'ops.notifications.title',
    group: 'platform',
    authority: { kind: 'facet', facet: 'notifications' },
    audience: 'ops',
  },
  {
    path: '/ops/notifications/sends',
    labelKey: 'ops.sends.title',
    group: 'platform',
    authority: { kind: 'staffAndFacet', facet: 'notifications' },
    audience: 'ops',
  },
  {
    path: '/ops/config/policy',
    labelKey: 'ops.config.policy.title',
    group: 'platform',
    authority: { kind: 'facet', facet: 'policy' },
    audience: 'ops',
  },
  {
    path: '/ops/credentials',
    labelKey: 'ops.credentials.title',
    group: 'platform',
    authority: { kind: 'staff' },
    audience: 'ops',
  },
  {
    path: '/ops/audit',
    labelKey: 'ops.audit.title',
    group: 'platform',
    authority: { kind: 'staff' },
    audience: 'ops',
  },
];

/**
 * Routed paths that are deliberately **not** navigable, each with its reason.
 *
 * The drift test asserts this list and `SCREENS` together account for every `<Route>` in
 * `app.tsx`, so a route added without a decision fails rather than quietly becoming the eleventh
 * screen with one door.
 */
export const NOT_NAVIGABLE: Readonly<Record<string, string>> = {
  '/login': 'the way in; a signed-in reader has no use for it',
  '*': 'the catch-all, which folds back to the landing page',
  '/': 'the landing page itself — the sidebar links away from it, never to it',
  '/orders/new': 'reached from the Orders list, where a Merchant decides to place one',
  '/orders/:reference': 'a record rather than a screen: reached from the list or a pasted link',
  '/orders/:reference/labels': 'reached from the Order whose parcels it prints',
  '/ops/shipments/:shipmentId': 'a record inside the board, which is why the back link survives',
  '/ops/config/tariffs/:merchantId/new': 'an editor opened for one Merchant from the tariff list',
  '/ops/_kit': 'the component gallery: not a screen an operator has any business finding',
};

/** Whether a live session satisfies one authority. The guards' own rules, stated once. */
export function holdsAuthority(session: LiveSession, authority: Authority): boolean {
  const isOps = session.role === 'opsUser';
  switch (authority.kind) {
    case 'session':
      return true;
    case 'staff':
      return isOps && session.platformStaff;
    case 'facet':
      return isOps && session.opsFacets.includes(authority.facet);
    case 'staffAndFacet':
      return isOps && session.platformStaff && session.opsFacets.includes(authority.facet);
    case 'accessAuthority':
      return isOps && (session.platformStaff || session.opsFacets.includes('access'));
  }
}

/**
 * The screens this session may open, in sidebar order.
 *
 * **Empty while an invitation is unanswered**, which is not a detail: until a new person accepts,
 * nothing else in the platform answers them, and the landing page renders their invitation and
 * nothing else (ADR-0067, amended). A sidebar that rendered anyway would offer navigation to
 * somebody who has none.
 */
export function reachableScreens(session: LiveSession): readonly Screen[] {
  if (session.awaitingInvitation === true) return [];
  const audience = session.role === 'merchantUser' ? 'merchant' : 'ops';
  return SCREENS.filter(
    (screen) =>
      (screen.audience === audience || screen.audience === 'any') &&
      holdsAuthority(session, screen.authority),
  );
}

/** The reachable screens gathered into their groups, with empty groups dropped. */
export function reachableGroups(
  session: LiveSession,
): readonly { readonly group: Group; readonly screens: readonly Screen[] }[] {
  const reachable = reachableScreens(session);
  return GROUPS.map((group) => ({
    group,
    screens: reachable.filter((screen) => screen.group === group),
  })).filter((entry) => entry.screens.length > 0);
}

/**
 * Which screen a path is on, and whether it *is* that screen or sits beneath it.
 *
 * Two answers rather than one, because the sidebar lists screens and the console has records:
 * `/ops/shipments/shp_1` is not a screen in the list, and marking the board `aria-current="page"`
 * while the reader is on a Shipment would be false. So an exact match is `'page'` and a
 * descendant is `'true'` — ARIA's own distinction between *this page* and *the current item in a
 * set*, which is exactly the difference here.
 *
 * **The most specific ancestor wins**, not the first match: for `/ops/config/tariffs/mch_1/new`
 * both `/ops` and `/ops/config/tariffs` are ancestors, and the tariff list is the one the reader
 * came through. The same rule the rate engine uses to pick a lane, for the same reason — a
 * first-match scan here would resolve by table order (ADR-0059).
 */
export function currentScreen(
  pathname: string,
  screens: readonly Screen[],
): { readonly screen: Screen; readonly current: 'page' | 'true' } | null {
  const exact = screens.find((screen) => screen.path === pathname);
  if (exact) return { screen: exact, current: 'page' };

  const ancestors = screens.filter((screen) => pathname.startsWith(`${screen.path}/`));
  if (ancestors.length === 0) return null;
  const deepest = ancestors.reduce((best, next) =>
    next.path.length > best.path.length ? next : best,
  );
  return { screen: deepest, current: 'true' };
}

/**
 * Which facet a refused screen needs, for the sentence a guard shows instead of redirecting.
 *
 * `null` wherever naming one would be false: a staff-only screen needs the cross-tenant bypass
 * rather than an area of responsibility, and `accessAuthority` is satisfied by either of two
 * things, so naming one of them would send a reader to ask for the wrong grant.
 */
export function facetNeededFor(path: string): OpsFacetName | null {
  const screen = SCREENS.find((candidate) => candidate.path === path);
  if (!screen) return null;
  const { authority } = screen;
  if (authority.kind === 'facet' || authority.kind === 'staffAndFacet') return authority.facet;
  return null;
}

/** Re-exported so a reader of this file need not go looking for the vocabulary. */
export { OPS_FACETS };
